import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  EventNote as EventNoteIcon,
  LocalShipping as LocalShippingIcon,
  PhotoCamera as PhotoCameraIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import type { PrivateUserInfoType } from '@otw/models';
import type { Contact } from '@otw/models/leads/contacts';
import type { ContactCompany } from '@otw/models/leads/contactCompanies';
import type { InventoryLead, InventoryLeadNote, InventoryLeadTruck } from '@otw/models/leads/inventoryLead';
import { useAuth } from 'contexts/AuthContext';
import { useContacts } from 'hooks/contacts/useContacts';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads';
import { usePickupLocations } from 'hooks/inventory/usePickupLocations';
import { useTerminals } from 'hooks/terminals/useTerminals';
import { getUserDisplayName, useInventoryAssignableUsers } from 'hooks/useUsers';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import {
  getVehicleModelOptionsByType,
  getVehicleSizeOptions,
  makeOptions,
  vehicleFuelOptions,
  vehicleTypeOptions,
} from 'constants/inventory';
import {
  getPickupLocationLabel,
  resolvePickupLocationById,
} from '../../util/pickupLocationDisplay';
import {
  buildTerminalByPickupLocationId,
  resolveCompanyDeliveryLocationIds,
} from 'views/SuperAdmin/Management/TerminalManagement/util/terminalCompanyLinks';
import { getTerminalDisplayName } from '@otw/models/leads/terminals';
import {
  resolveStoragePathsToUrls,
  uploadInventoryLeadTruckPhotos,
} from '../../util/inventoryLeadPhotos';

/** Colors the Gray Book models recognize; "Being painted" is not a lead state. */
const truckColorOptions = ['White', 'Blue', 'Yellow'];

const emptyTruck = (): InventoryLeadTruck => ({
  make: '',
  model: '',
  year: 0,
  size: '',
  type: '',
  fuel: 'Gas',
  vin: '',
  hasLien: null,
});

const VIN_LENGTH = 17;

/** Uppercase + strip spaces; cap at 17 like VehicleForm. */
const normalizeVinInput = (raw: string): string =>
  raw.replace(/\s/g, '').toUpperCase().slice(0, VIN_LENGTH);

/** Empty is allowed on leads; a partial VIN must be exactly 17 chars. */
const isVinValid = (vin?: string | null): boolean => {
  const trimmed = vin?.trim() ?? '';
  return trimmed.length === 0 || trimmed.length === VIN_LENGTH;
};

const isTruckValid = (truck: InventoryLeadTruck): boolean =>
  Boolean(
    truck.make?.trim() &&
      truck.model?.trim() &&
      truck.year &&
      truck.size?.trim() &&
      truck.type?.trim() &&
      truck.fuel &&
      isVinValid(truck.vin),
  );

const cleanTruck = (t: InventoryLeadTruck): InventoryLeadTruck => ({
  make: t.make.trim(),
  model: t.model.trim(),
  year: Number(t.year),
  size: t.size.trim(),
  type: t.type.trim(),
  fuel: t.fuel,
  ...(t.vin?.trim() ? { vin: normalizeVinInput(t.vin) } : {}),
  ...(typeof t.mileage === 'number' && Number.isFinite(t.mileage)
    ? { mileage: t.mileage }
    : {}),
  ...(typeof t.askingPrice === 'number' && Number.isFinite(t.askingPrice)
    ? { askingPrice: t.askingPrice }
    : {}),
  ...(t.color?.trim() ? { color: t.color.trim() } : {}),
  ...(t.hasLien === true || t.hasLien === false ? { hasLien: t.hasLien } : {}),
  ...(t.hasLien === true
    ? {
        ...(t.lienLender?.trim() ? { lienLender: t.lienLender.trim() } : {}),
        ...(typeof t.lienPayoffAmount === 'number' &&
        Number.isFinite(t.lienPayoffAmount)
          ? { lienPayoffAmount: t.lienPayoffAmount }
          : {}),
        ...(t.lienGoodThroughDate?.trim()
          ? { lienGoodThroughDate: t.lienGoodThroughDate.trim() }
          : {}),
        ...(t.lienAccountNumber?.trim()
          ? { lienAccountNumber: t.lienAccountNumber.trim() }
          : {}),
      }
    : {}),
  ...(t.photoStoragePaths?.length
    ? { photoStoragePaths: t.photoStoragePaths }
    : {}),
  pickupLocationId: t.pickupLocationId?.trim() ? t.pickupLocationId.trim() : null,
  repairShopLocationId: t.repairShopLocationId?.trim()
    ? t.repairShopLocationId.trim()
    : null,
  ...(typeof t.shippingMiles === 'number' && Number.isFinite(t.shippingMiles)
    ? { shippingMiles: t.shippingMiles }
    : {}),
  ...(typeof t.shippingCost === 'number' && Number.isFinite(t.shippingCost)
    ? { shippingCost: t.shippingCost }
    : {}),
  ...(typeof t.estimated_body === 'number' && Number.isFinite(t.estimated_body)
    ? { estimated_body: t.estimated_body }
    : {}),
  ...(typeof t.estimated_mechanical === 'number' && Number.isFinite(t.estimated_mechanical)
    ? { estimated_mechanical: t.estimated_mechanical }
    : {}),
  ...(typeof t.estimated_safety === 'number' && Number.isFinite(t.estimated_safety)
    ? { estimated_safety: t.estimated_safety }
    : {}),
  ...(typeof t.estimated_repair_cost === 'number' && Number.isFinite(t.estimated_repair_cost)
    ? { estimated_repair_cost: t.estimated_repair_cost }
    : {}),
  ...(t.estimated_repair_addons?.length
    ? { estimated_repair_addons: t.estimated_repair_addons }
    : {}),
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('en-US');

const truckHeading = (truck: InventoryLeadTruck, index: number): string => {
  const label = [truck.year || '', truck.make, truck.model]
    .filter(Boolean)
    .join(' ')
    .trim();
  return label || `Truck ${index + 1}`;
};

const FormSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, action, children }) => {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.75, md: 2.25 },
        borderRadius: 2.5,
        borderColor: alpha(theme.palette.divider, 0.8),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ mb: 2 }}
        useFlexGap
        flexWrap="wrap"
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
};

const FieldGroupLabel: React.FC<{ icon?: React.ReactNode; label: string }> = ({
  icon,
  label,
}) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
    {icon}
    <Typography
      variant="caption"
      fontWeight={800}
      color="text.secondary"
      sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
  </Stack>
);

const PhotoThumb: React.FC<{
  src?: string;
  alt: string;
  pending?: boolean;
  onRemove: () => void;
}> = ({ src, alt, pending = false, onRemove }) => (
  <Box
    sx={{
      position: 'relative',
      width: 76,
      height: 76,
      borderRadius: 1.5,
      overflow: 'hidden',
      border: pending ? '1px dashed' : '1px solid',
      borderColor: pending ? 'primary.main' : 'divider',
      bgcolor: 'action.hover',
    }}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : null}
    <IconButton
      size="small"
      onClick={onRemove}
      aria-label={`Remove ${alt}`}
      sx={{
        position: 'absolute',
        top: 2,
        right: 2,
        bgcolor: alpha('#000', 0.45),
        color: 'common.white',
        '&:hover': { bgcolor: alpha('#000', 0.65) },
        p: 0.25,
      }}
    >
      <DeleteIcon sx={{ fontSize: 14 }} />
    </IconButton>
  </Box>
);

const PendingPhotoThumb: React.FC<{ file: File; onRemove: () => void }> = ({
  file,
  onRemove,
}) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return <PhotoThumb src={url} alt={file.name} pending onRemove={onRemove} />;
};

export interface InventoryLeadFormProps {
  /** When set, the form edits this lead instead of creating a new one. */
  selectedLead?: InventoryLead | null;
  /** Prefills + locks the contact search to this id when the contact resolves. */
  initialContactId?: string | null;
  /** Prefills + locks the company search to this id when the company resolves. */
  initialCompanyId?: string | null;
  onCreated?: (lead: InventoryLead) => void | Promise<void>;
  onUpdated?: (lead: InventoryLead) => void | Promise<void>;
  onCancel: () => void;
}

export const InventoryLeadForm: React.FC<InventoryLeadFormProps> = ({
  selectedLead = null,
  initialContactId = null,
  initialCompanyId = null,
  onCreated,
  onUpdated,
  onCancel,
}) => {
  const theme = useTheme();
  const { userInfo } = useAuth();
  const { contacts } = useContacts();
  const { companies } = useContactCompanies();
  const { pickupLocations } = usePickupLocations();
  const { terminals } = useTerminals();
  const terminalByLocationId = useMemo(
    () => buildTerminalByPickupLocationId(terminals),
    [terminals],
  );
  const { users: inventoryReps } = useInventoryAssignableUsers();
  const { createInventoryLead, updateInventoryLead } = useInventoryLeads();
  const { showError } = useSnackbar();
  const isEditing = Boolean(selectedLead);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const repPrefilled = useRef(false);

  const [contact, setContact] = useState<Contact | null>(null);
  const [company, setCompany] = useState<ContactCompany | null>(null);
  const [assignedRep, setAssignedRep] = useState<PrivateUserInfoType | null>(null);
  const [trucks, setTrucks] = useState<InventoryLeadTruck[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<Record<number, File[]>>({});
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Prefill from the lead being edited once contacts/companies are available.
  useEffect(() => {
    if (!selectedLead) return;
    const matchContact = contacts.find((c) => c.id === selectedLead.contactId);
    if (matchContact) setContact(matchContact);
    const matchCompany = companies.find((c) => c.id === selectedLead.companyId);
    if (matchCompany) setCompany(matchCompany);
    setTrucks(
      selectedLead.trucks?.length
        ? selectedLead.trucks.map((t) => ({
            ...t,
            vin: t.vin ?? '',
            hasLien: t.hasLien ?? null,
            lienLender: t.lienLender ?? null,
            lienPayoffAmount: t.lienPayoffAmount ?? null,
            lienGoodThroughDate: t.lienGoodThroughDate ?? null,
            lienAccountNumber: t.lienAccountNumber ?? null,
            pickupLocationId: t.pickupLocationId ?? null,
          }))
        : [],
    );
    setNoteText(selectedLead.note?.text ?? '');
  }, [selectedLead, contacts, companies]);

  // Only prefill once — a users refetch must not re-add a rep the user cleared.
  useEffect(() => {
    if (repPrefilled.current || !selectedLead?.assignedRepId) return;
    const match = inventoryReps.find(
      (user) => String(user.id) === String(selectedLead.assignedRepId),
    );
    if (match) {
      setAssignedRep(match);
      repPrefilled.current = true;
    }
  }, [selectedLead, inventoryReps]);

  useEffect(() => {
    if (selectedLead || contact || !initialContactId) return;
    const match = contacts.find((c) => c.id === initialContactId);
    if (match) setContact(match);
  }, [contacts, initialContactId, contact, selectedLead]);

  useEffect(() => {
    if (selectedLead || company || !initialCompanyId) return;
    const match = companies.find((c) => c.id === initialCompanyId);
    if (match) setCompany(match);
  }, [companies, initialCompanyId, company, selectedLead]);

  // When company is set on create, default empty truck pickups to first delivery location.
  useEffect(() => {
    if (selectedLead || !company) return;
    const deliveryIds = resolveCompanyDeliveryLocationIds(company, terminals);
    const first = deliveryIds[0];
    if (!first) return;
    setTrucks((prev) =>
      prev.map((t) =>
        t.pickupLocationId?.trim() ? t : { ...t, pickupLocationId: first },
      ),
    );
  }, [company, selectedLead, terminals]);

  // Resolve existing photo URLs for thumbnails.
  useEffect(() => {
    const paths = trucks.flatMap((t) => t.photoStoragePaths ?? []);
    if (!paths.length) return;
    let cancelled = false;
    resolveStoragePathsToUrls(paths).then((urls) => {
      if (!cancelled) setPhotoUrls((prev) => ({ ...prev, ...urls }));
    });
    return () => {
      cancelled = true;
    };
  }, [trucks]);

  const companyDropoffOptions = useMemo(() => {
    if (!company) return [];
    const ids = resolveCompanyDeliveryLocationIds(company, terminals);
    return ids
      .map((id) => resolvePickupLocationById(pickupLocations, id))
      .filter((loc): loc is NonNullable<typeof loc> => Boolean(loc));
  }, [company, pickupLocations, terminals]);

  const defaultPickupId = useMemo(() => {
    if (!company) return '';
    return resolveCompanyDeliveryLocationIds(company, terminals)[0] ?? '';
  }, [company, terminals]);

  const updateTruck = (index: number, updates: Partial<InventoryLeadTruck>) => {
    setTrucks((prev) => prev.map((t, i) => (i === index ? { ...t, ...updates } : t)));
  };

  const addTruck = () =>
    setTrucks((prev) => [
      ...prev,
      {
        ...emptyTruck(),
        ...(defaultPickupId ? { pickupLocationId: defaultPickupId } : {}),
      },
    ]);
  const removeTruck = (index: number) => {
    setTrucks((prev) => prev.filter((_, i) => i !== index));
    setPendingPhotos((prev) => {
      const next: Record<number, File[]> = {};
      Object.entries(prev).forEach(([key, files]) => {
        const i = Number(key);
        if (i < index) next[i] = files;
        else if (i > index) next[i - 1] = files;
      });
      return next;
    });
  };

  /** Empty list is allowed; any trucks present must be fully valid. */
  const trucksValid = trucks.every(isTruckValid);
  const canSubmit = useMemo(
    () => Boolean(contact && company && trucksValid),
    [contact, company, trucksValid],
  );

  const totalAsking = useMemo(
    () =>
      trucks.reduce(
        (sum, truck) =>
          typeof truck.askingPrice === 'number' && Number.isFinite(truck.askingPrice)
            ? sum + truck.askingPrice
            : sum,
        0,
      ),
    [trucks],
  );

  const handleFilesSelected = (truckIndex: number, fileList: FileList | null) => {
    if (!fileList?.length) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    setPendingPhotos((prev) => ({
      ...prev,
      [truckIndex]: [...(prev[truckIndex] ?? []), ...files],
    }));
  };

  const removePendingPhoto = (truckIndex: number, fileIndex: number) => {
    setPendingPhotos((prev) => ({
      ...prev,
      [truckIndex]: (prev[truckIndex] ?? []).filter((_, i) => i !== fileIndex),
    }));
  };

  const removeExistingPhoto = (truckIndex: number, path: string) => {
    updateTruck(truckIndex, {
      photoStoragePaths: (trucks[truckIndex].photoStoragePaths ?? []).filter((p) => p !== path),
    });
  };

  const buildNoteForSave = (): InventoryLeadNote | null | undefined => {
    const trimmed = noteText.trim();
    const existing = selectedLead?.note?.text?.trim() ?? '';
    if (trimmed === existing) return undefined;
    if (!trimmed) return null;
    if (!userInfo) return undefined;
    return {
      text: trimmed,
      author: userInfo.id,
      updatedAt: new Date().toISOString(),
    };
  };

  const attachPendingPhotos = async (
    leadId: string,
    baseTrucks: InventoryLeadTruck[],
  ): Promise<InventoryLeadTruck[]> => {
    const next = [...baseTrucks];
    for (let i = 0; i < next.length; i++) {
      const files = pendingPhotos[i] ?? [];
      if (!files.length) continue;
      const uploaded = await uploadInventoryLeadTruckPhotos(leadId, i, files);
      next[i] = {
        ...next[i],
        photoStoragePaths: [...(next[i].photoStoragePaths ?? []), ...uploaded],
      };
    }
    return next;
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!contact || !company || !trucksValid) return;
    setSubmitting(true);
    const assignedRepId = assignedRep ? String(assignedRep.id) : null;
    try {
      let cleanedTrucks = trucks.map(cleanTruck);
      const noteUpdate = buildNoteForSave();

      if (selectedLead) {
        cleanedTrucks = await attachPendingPhotos(selectedLead.id, cleanedTrucks);
        await updateInventoryLead({
          id: selectedLead.id,
          updates: {
            contactId: contact.id,
            companyId: company.id,
            trucks: cleanedTrucks,
            assignedRepId,
            ...(noteUpdate !== undefined ? { note: noteUpdate } : {}),
          },
        });
        await onUpdated?.({
          ...selectedLead,
          contactId: contact.id,
          companyId: company.id,
          trucks: cleanedTrucks,
          assignedRepId,
          ...(noteUpdate !== undefined ? { note: noteUpdate } : {}),
          updatedAt: new Date(),
        });
      } else {
        const lead = await createInventoryLead({
          contactId: contact.id,
          companyId: company.id,
          trucks: cleanedTrucks,
          status: 'pendingList',
          assignedRepId,
          ...(noteUpdate ? { note: noteUpdate } : {}),
        });
        const hasPending = Object.values(pendingPhotos).some((files) => files.length > 0);
        if (hasPending) {
          const withPhotos = await attachPendingPhotos(lead.id, cleanedTrucks);
          await updateInventoryLead({
            id: lead.id,
            updates: { trucks: withPhotos },
          });
          await onCreated?.({ ...lead, trucks: withPhotos });
        } else {
          await onCreated?.(lead);
        }
      }
    } catch {
      showError(
        isEditing ? 'Could not update inventory lead' : 'Could not create inventory lead',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <FormSection
        icon={<BusinessIcon fontSize="small" />}
        title="Seller & ownership"
        subtitle="Who we're buying from, and the inventory rep working the deal."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={contacts}
              value={contact}
              onChange={(_, next) => setContact(next)}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contact"
                  required
                  error={touched && !contact}
                  helperText={touched && !contact ? 'Select a contact' : ' '}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={companies}
              value={company}
              onChange={(_, next) => {
                setCompany(next);
                const first = next
                  ? resolveCompanyDeliveryLocationIds(next, terminals)[0] ?? ''
                  : '';
                if (first) {
                  setTrucks((prev) =>
                    prev.map((t) =>
                      t.pickupLocationId?.trim() ? t : { ...t, pickupLocationId: first },
                    ),
                  );
                }
              }}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  required
                  error={touched && !company}
                  helperText={touched && !company ? 'Select a company' : ' '}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={inventoryReps}
              value={assignedRep}
              onChange={(_, next) => setAssignedRep(next)}
              getOptionLabel={(option) => getUserDisplayName(option)}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={String(option.id)}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        bgcolor: alpha(theme.palette.primary.main, 0.14),
                        color: 'primary.main',
                        fontWeight: 700,
                      }}
                    >
                      {getUserDisplayName(option).slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {getUserDisplayName(option)}
                      </Typography>
                      {option.email ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Inventory rep"
                  helperText={
                    inventoryReps.length === 0
                      ? 'No users are assigned to the inventory department yet'
                      : ' '
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        icon={<EventNoteIcon fontSize="small" />}
        title="Notes"
        subtitle="Optional — add context about this lead for your team."
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Note"
          placeholder="Seller preferences, negotiation context, next steps..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          helperText={
            selectedLead?.note
              ? `Last updated by a team member · ${new Date(selectedLead.note.updatedAt).toLocaleString('en-US')}`
              : ' '
          }
        />
      </FormSection>

      <FormSection
        icon={<LocalShippingIcon fontSize="small" />}
        title="Trucks"
        subtitle="Optional — add trucks when ready. Each can have its own asking price, pickup, and photos."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {totalAsking > 0 ? (
              <Chip
                size="small"
                label={`Total ask ${currencyFormatter.format(totalAsking)}`}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: 'success.dark',
                }}
              />
            ) : null}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addTruck}
              sx={{ textTransform: 'none' }}
            >
              Add truck
            </Button>
          </Stack>
        }
      >
        <Stack spacing={2}>
          {trucks.map((truck, index) => {
            const sizeOptions = getVehicleSizeOptions(truck.type) ?? [];
            const modelOptions = getVehicleModelOptionsByType(truck.make, truck.type) ?? [];
            const existingPaths = truck.photoStoragePaths ?? [];
            const pending = pendingPhotos[index] ?? [];
            const photoCount = existingPaths.length + pending.length;
            return (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  borderColor: alpha(theme.palette.primary.main, 0.18),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.14),
                  }}
                >
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      fontSize: 12,
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                      color: 'primary.main',
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                    noWrap
                  >
                    {truckHeading(truck, index)}
                  </Typography>
                  {typeof truck.mileage === 'number' && Number.isFinite(truck.mileage) ? (
                    <Chip
                      size="small"
                      label={`${numberFormatter.format(truck.mileage)} mi`}
                      sx={{ height: 22, fontWeight: 600 }}
                    />
                  ) : null}
                  {typeof truck.askingPrice === 'number' &&
                  Number.isFinite(truck.askingPrice) ? (
                    <Chip
                      size="small"
                      label={currencyFormatter.format(truck.askingPrice)}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        color: 'success.dark',
                      }}
                    />
                  ) : null}
                  {photoCount > 0 ? (
                    <Chip
                      size="small"
                      icon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
                      label={photoCount}
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        '& .MuiChip-icon': { ml: 0.5 },
                      }}
                    />
                  ) : null}
                  <Tooltip title="Remove truck">
                    <IconButton
                      size="small"
                      onClick={() => removeTruck(index)}
                      aria-label={`Remove truck ${index + 1}`}
                      sx={{
                        color: 'error.main',
                        '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12) },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Box>
                      <FieldGroupLabel
                        icon={
                          <LocalShippingIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        }
                        label="Vehicle"
                      />
                      <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={4}>
                          <Autocomplete
                            freeSolo
                            options={makeOptions}
                            value={truck.make}
                            onChange={(_, next) => updateTruck(index, { make: next || '' })}
                            onInputChange={(_, next) => updateTruck(index, { make: next })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Make"
                                required
                                size="small"
                                error={touched && !truck.make.trim()}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Autocomplete
                            freeSolo
                            options={vehicleTypeOptions}
                            value={truck.type}
                            onChange={(_, next) => updateTruck(index, { type: next || '' })}
                            onInputChange={(_, next) => updateTruck(index, { type: next })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Type"
                                required
                                size="small"
                                error={touched && !truck.type.trim()}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Autocomplete
                            freeSolo
                            options={modelOptions}
                            value={truck.model}
                            onChange={(_, next) => updateTruck(index, { model: next || '' })}
                            onInputChange={(_, next) => updateTruck(index, { model: next })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Model"
                                required
                                size="small"
                                error={touched && !truck.model.trim()}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Year"
                            type="number"
                            required
                            fullWidth
                            size="small"
                            value={truck.year || ''}
                            onChange={(e) =>
                              updateTruck(index, { year: Number(e.target.value) })
                            }
                            error={touched && !truck.year}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Autocomplete
                            freeSolo
                            options={sizeOptions}
                            value={truck.size}
                            onChange={(_, next) => updateTruck(index, { size: next || '' })}
                            onInputChange={(_, next) => updateTruck(index, { size: next })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Size"
                                required
                                size="small"
                                error={touched && !truck.size.trim()}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            select
                            label="Fuel"
                            required
                            fullWidth
                            size="small"
                            value={truck.fuel}
                            onChange={(e) =>
                              updateTruck(index, { fuel: e.target.value as 'Gas' | 'Diesel' })
                            }
                          >
                            {vehicleFuelOptions.map((fuel) => (
                              <MenuItem key={fuel} value={fuel}>
                                {fuel}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <FieldGroupLabel
                        icon={
                          <AttachMoneyIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        }
                        label="Valuation"
                      />
                      <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Mileage"
                            type="number"
                            fullWidth
                            size="small"
                            value={truck.mileage ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updateTruck(index, {
                                mileage: raw === '' ? undefined : Number(raw),
                              });
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Asking price"
                            type="number"
                            fullWidth
                            size="small"
                            value={truck.askingPrice ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updateTruck(index, {
                                askingPrice: raw === '' ? undefined : Number(raw),
                              });
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            select
                            label="Color"
                            fullWidth
                            size="small"
                            value={truck.color ?? ''}
                            onChange={(e) =>
                              updateTruck(index, { color: e.target.value || undefined })
                            }
                          >
                            <MenuItem value="">
                              <em>Unspecified</em>
                            </MenuItem>
                            {truckColorOptions.map((color) => (
                              <MenuItem key={color} value={color}>
                                {color}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <FieldGroupLabel
                        icon={<PlaceIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                        label="Identification & pickup"
                      />
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <TextField
                            label="VIN"
                            fullWidth
                            size="small"
                            value={truck.vin || ''}
                            onChange={(e) =>
                              updateTruck(index, { vin: normalizeVinInput(e.target.value) })
                            }
                            inputProps={{ maxLength: VIN_LENGTH }}
                            InputLabelProps={{ shrink: Boolean(truck.vin) }}
                            error={!isVinValid(truck.vin)}
                            helperText={`${(truck.vin || '').length}/${VIN_LENGTH}`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl component="fieldset" fullWidth>
                            <Typography variant="subtitle2" gutterBottom>
                              Is there a lien?
                            </Typography>
                            <RadioGroup
                              row
                              value={
                                truck.hasLien === true
                                  ? 'yes'
                                  : truck.hasLien === false
                                    ? 'no'
                                    : ''
                              }
                              onChange={(e) => {
                                const value =
                                  e.target.value === 'yes'
                                    ? true
                                    : e.target.value === 'no'
                                      ? false
                                      : null;
                                updateTruck(index, {
                                  hasLien: value,
                                  ...(value !== true
                                    ? {
                                        lienLender: null,
                                        lienPayoffAmount: null,
                                        lienGoodThroughDate: null,
                                        lienAccountNumber: null,
                                      }
                                    : {}),
                                });
                              }}
                            >
                              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        {truck.hasLien === true ? (
                          <>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Lender"
                                fullWidth
                                size="small"
                                value={truck.lienLender ?? ''}
                                onChange={(e) =>
                                  updateTruck(index, {
                                    lienLender: e.target.value || null,
                                  })
                                }
                                InputLabelProps={{ shrink: Boolean(truck.lienLender) }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Payoff amount"
                                type="number"
                                fullWidth
                                size="small"
                                value={truck.lienPayoffAmount ?? ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  updateTruck(index, {
                                    lienPayoffAmount:
                                      raw === '' ? null : Number(raw),
                                  });
                                }}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label="Good through date"
                                  value={
                                    truck.lienGoodThroughDate
                                      ? dayjs(truck.lienGoodThroughDate)
                                      : null
                                  }
                                  onChange={(date) =>
                                    updateTruck(index, {
                                      lienGoodThroughDate: date
                                        ? date.toISOString()
                                        : null,
                                    })
                                  }
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      size: 'small',
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Account number"
                                fullWidth
                                size="small"
                                value={truck.lienAccountNumber ?? ''}
                                onChange={(e) =>
                                  updateTruck(index, {
                                    lienAccountNumber: e.target.value || null,
                                  })
                                }
                                InputLabelProps={{
                                  shrink: Boolean(truck.lienAccountNumber),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {(() => {
                                const asking = Number(truck.askingPrice) || 0;
                                const payoff = Number(truck.lienPayoffAmount) || 0;
                                const equity = asking - payoff;
                                return (
                                  <TextField
                                    label="Equity"
                                    fullWidth
                                    size="small"
                                    value={equity.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                    InputProps={{ readOnly: true }}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                );
                              })()}
                            </Grid>
                          </>
                        ) : null}
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Pickup location"
                            value={truck.pickupLocationId ?? ''}
                            onChange={(e) =>
                              updateTruck(index, {
                                pickupLocationId: e.target.value || null,
                              })
                            }
                            disabled={!company}
                            helperText={
                              !company
                                ? 'Select a company first'
                                : companyDropoffOptions.length === 0
                                  ? 'This company has no dropoff locations yet'
                                  : ' '
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {companyDropoffOptions.map((loc) => {
                              const terminal = loc.id
                                ? terminalByLocationId.get(loc.id)
                                : undefined
                              return (
                                <MenuItem key={loc.id} value={loc.id}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <span>
                                      {getPickupLocationLabel(loc, {
                                        terminalLabel: terminal
                                          ? getTerminalDisplayName(terminal, loc)
                                          : undefined,
                                      })}
                                    </span>
                                    {terminal ? (
                                      <Chip
                                        size="small"
                                        label="Terminal"
                                        color="info"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    ) : null}
                                  </Stack>
                                </MenuItem>
                              )
                            })}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <FieldGroupLabel
                          icon={
                            <PhotoCameraIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          }
                          label={`Photos${photoCount ? ` (${photoCount})` : ''}`}
                        />
                        <Button
                          size="small"
                          startIcon={<PhotoCameraIcon />}
                          onClick={() => fileInputRefs.current[index]?.click()}
                          sx={{ textTransform: 'none' }}
                        >
                          Add photos
                        </Button>
                        <input
                          ref={(el) => {
                            fileInputRefs.current[index] = el;
                          }}
                          type="file"
                          accept="image/*"
                          multiple
                          hidden
                          onChange={(e) => {
                            handleFilesSelected(index, e.target.files);
                            e.target.value = '';
                          }}
                        />
                      </Stack>
                      {photoCount === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No photos yet — add shots of the truck for the negotiation review.
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {existingPaths.map((path) => (
                            <PhotoThumb
                              key={path}
                              src={photoUrls[path]}
                              alt="Truck photo"
                              onRemove={() => removeExistingPhoto(index, path)}
                            />
                          ))}
                          {pending.map((file, fileIndex) => (
                            <PendingPhotoThumb
                              key={`${file.name}-${file.size}-${fileIndex}`}
                              file={file}
                              onRemove={() => removePendingPhoto(index, fileIndex)}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </FormSection>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1.5}
        sx={{ pt: 0.5 }}
      >
        {touched && !canSubmit ? (
          <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
            {trucks.length > 0
              ? 'Fill in the contact, company, and required truck fields.'
              : 'Select a contact and company to continue.'}
          </Typography>
        ) : null}
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || (touched && !canSubmit)}
        >
          {isEditing ? 'Save changes' : 'Create inventory lead'}
        </Button>
      </Stack>
    </Stack>
  );
};
